import { Dispatch, SetStateAction, useEffect } from "react";
import { loadCountryConfigFiles } from "../countryConfigLoader";
import {
  addDisbursementDate,
  addRecurringCadence,
} from "pages/employer-of-record/payments/utils";
import { AdditionalPayContextProps } from "pages/employer-of-record/payments/contexts/additionalPayContext";
import { FieldConfig, type Option } from "../../SetupFormGenerator";
import {
  BenefitsAndAllowancesPayloadType,
  CountryConfigKeyType,
  SubdivisionConfigKeyType,
} from "../types";
import { useFormikContext } from "formik";
import { AdditionalPayFormData } from "../../../types";
import { MessageWithLinkType } from "pages/employer-of-record/components/MessageWithLink/types";
import { CountryCode } from "pages/employer-of-record/onboarding/constants";
import { TaxableBenefits } from "../countryConfigs/DE/constants";

const useCountryConfigFetcher = (
  additionalPayContext: AdditionalPayContextProps | null,
  setPaymentSettingsFields: Dispatch<SetStateAction<FieldConfig[]>>,
  setTaxableBenefitsValues: Dispatch<SetStateAction<string[]>>,
  setLink: Dispatch<SetStateAction<string | undefined>>,
  setMessage: Dispatch<SetStateAction<MessageWithLinkType | null>>
) => {
  const selectedCountry = additionalPayContext?.selectedCountry;

  const { values } =
    useFormikContext<AdditionalPayFormData<BenefitsAndAllowancesPayloadType>>();

  const selectedSubdivision = additionalPayContext?.selectedSubdivision;
  const isLocationReady = additionalPayContext?.isLocationReady ?? false;

  useEffect(() => {
    const fetchCountryConfigs = () => {
      if (!isLocationReady) return;
      const configs = loadCountryConfigFiles(
        selectedCountry as CountryConfigKeyType,
        selectedSubdivision as SubdivisionConfigKeyType
      );

      let paymentSettingsFormFields = configs.PAYMENT_SETTINGS_FIELDS;
      if (additionalPayContext?.disbursementDateOptions) {
        paymentSettingsFormFields = addDisbursementDate(
          paymentSettingsFormFields,
          Object.values(additionalPayContext.disbursementDateOptions)
        );
      }
      if (additionalPayContext?.recurringCadenceOptions) {
        paymentSettingsFormFields = addRecurringCadence(
          paymentSettingsFormFields,
          Object.values(additionalPayContext.recurringCadenceOptions)
        );
      }

      // For Germany + Sachbezug Allowance, only show non-cash option
      const benefitType = values.setUp.payload.paymentSettings.benefitType;
      if (
        selectedCountry === CountryCode.DE &&
        benefitType === TaxableBenefits.SACHBEZUG_ALLOWANCE
      ) {
        paymentSettingsFormFields = paymentSettingsFormFields.map(
          (field: FieldConfig) => {
            if (field.name === "formOfIncome" && field.options) {
              return {
                ...field,
                options: field.options.filter(
                  (option: Option) => option.value === "non-cash"
                ),
              };
            }
            return field;
          }
        );
      }

      setPaymentSettingsFields(paymentSettingsFormFields);
      setTaxableBenefitsValues(configs.TAXABLEBENFITSVALUES);

      "TAXABLEBENEFITSLINK" in configs
        ? setLink(configs.TAXABLEBENEFITSLINK)
        : setLink(undefined);

      const defaultMessage = configs.TaxableBenefitsTypeMessage?.[""] || null;

      const message =
        configs.TaxableBenefitsTypeMessage?.[
          values.setUp.payload.paymentSettings.benefitType || ""
        ] || defaultMessage;

      setMessage(message);
    };

    fetchCountryConfigs();
  }, [
    isLocationReady,
    selectedCountry,
    selectedSubdivision,
    additionalPayContext?.disbursementDateOptions,
    setTaxableBenefitsValues,
    setPaymentSettingsFields,
    setLink,
    setMessage,
    additionalPayContext?.recurringCadenceOptions,
    values.setUp.payload.paymentSettings.benefitType,
  ]);
};

export default useCountryConfigFetcher;
